import { useEffect, useRef, useState } from "react";
import { PortalApiError, portalFetch } from "../api/client";
import type { PortalProposalDto } from "../model";
import { StatusBanner } from "./StatusBanner";

export function ProposalView({ proposalId }: { proposalId: string }) {
	const [proposal, setProposal] = useState<PortalProposalDto>();
	const [draft, setDraft] = useState("");
	const [status, setStatus] = useState("");
	const statusRef = useRef<HTMLDivElement>(null);
	useEffect(() => {
		portalFetch<PortalProposalDto>(`/api/portal/v1/proposals/${proposalId}`).then((value) => {
			setProposal(value);
			setDraft(value.draftContent);
		});
	}, [proposalId]);
	async function saveDraft(current: PortalProposalDto) {
		if (draft === current.draftContent) return current;
		const next = await portalFetch<PortalProposalDto>(`/api/portal/v1/proposals/${proposalId}`, {
			method: "PATCH",
			body: JSON.stringify({ draftContent: draft, expectedDraftVersion: current.draftVersion }),
		});
		setProposal(next);
		return next;
	}
	async function save() {
		if (!proposal) return;
		try {
			await saveDraft(proposal);
			setStatus("Draft saved.");
		} catch (error) {
			setStatus(
				error instanceof PortalApiError && error.code === "staleDraft"
					? "This proposal changed. Reload before saving again."
					: "The draft could not be saved.",
			);
		}
		statusRef.current?.focus();
	}
	async function submit() {
		if (!proposal) return;
		try {
			const saved = await saveDraft(proposal);
			const next = await portalFetch<PortalProposalDto>(`/api/portal/v1/proposals/${proposalId}/submit`, {
				method: "POST",
				body: JSON.stringify({
					expectedDraftVersion: saved.draftVersion,
					sourceTurnIds: [],
				}),
			});
			setProposal(next);
			setStatus(next.status === "accepted" ? "Requirements updated." : "Proposal saved and sent for review.");
		} catch (error) {
			setStatus(
				error instanceof PortalApiError && error.code === "staleDraft"
					? "This proposal changed. Reload before submitting again."
					: "The proposal could not be saved and submitted.",
			);
		}
		statusRef.current?.focus();
	}
	async function withdraw() {
		if (!proposal?.latestRevision) return;
		const next = await portalFetch<PortalProposalDto>(`/api/portal/v1/proposals/${proposalId}/withdraw`, {
			method: "POST",
			body: JSON.stringify({
				revisionId: proposal.latestRevision.id,
				revisionHash: proposal.latestRevision.contentHash,
			}),
		});
		setProposal(next);
		setStatus("Proposal withdrawn. You can revise the retained draft.");
		statusRef.current?.focus();
	}
	if (!proposal) return <StatusBanner kind="info">Loading proposal…</StatusBanner>;
	return (
		<section className="proposal-editor">
			<p className="eyebrow">{proposal.kind}</p>
			<h1 tabIndex={-1}>{proposal.title}</h1>
			<p className="visibility-note">
				Your proposal and its revisions are part of your shared project record with Neal. Project Agent conversation
				begins in Discovery after the proposal is accepted.
			</p>
			<section className="proposal-draft-panel" aria-labelledby="proposal-draft-heading">
				<p className="step-label">THE PROPOSAL DOCUMENT</p>
				<h2 id="proposal-draft-heading">Write the proposal</h2>
				<p>This is the proposal text Neal will review when you submit a revision.</p>
				<label htmlFor="proposal-draft">Proposal text</label>
				<textarea
					id="proposal-draft"
					value={draft}
					disabled={!(["draft", "withdrawn", "rejected"] as string[]).includes(proposal.status)}
					onChange={(event) => setDraft(event.target.value)}
				/>
				<div className="action-row">
					{(["draft", "withdrawn", "rejected"] as string[]).includes(proposal.status) && (
						<>
							<button type="button" onClick={save}>
								Save draft
							</button>
							<button type="button" onClick={submit} disabled={proposal.status !== "draft"}>
								Submit revision
							</button>
						</>
					)}
					{proposal.status === "submitted" && proposal.latestRevision && (
						<button type="button" onClick={withdraw}>
							Withdraw proposal
						</button>
					)}
				</div>
				<div ref={statusRef} tabIndex={-1} aria-live="polite">
					{status}
				</div>
			</section>
		</section>
	);
}
